import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth } from '@/config/firebase';
import { useAuthStore } from '@/store/authStore';
import { useTenantStore } from '@/store/tenantStore';
import { useLocalizationStore } from '@/store/localizationStore';
import {
  searchJobs,
  getSearchAggregations,
  getAppliedSavedJobs,
  getSavedJobsCount,
  saveJob,
  unsaveJob,
} from '@/services/api/homeApi';
import { getUserDetails } from '@/services/api/authApi';
import { extractResumeSkills } from '@/services/api/applicationApi';
import { analyticsService } from '@/services/analytics/analyticsService';
import {
  defaultSortFilters,
  applyFilterSelection,
  getSupportedFilterKeys,
  hasSearchTerms,
  sortSelectedOnTop,
  withCheckedState,
  type SelectedFilters,
} from '@/features/jobs/utils/filterState';
import {
  isAgreedWithDataPolicy,
  setAgreedWithDataPolicy,
  validateResumeFile,
} from '@/features/jobs/utils/resumeFile';
import { EXTRACTED_RESUME_KEY } from '@/features/jobs/hooks/useExtractedResume';
import { readAppliedSavedCache, withAppliedSaved } from '@/features/home/utils/appliedSavedCache';
import { appendNewJobs } from '../utils/jobPages';
import type {
  SearchInput,
  SearchResponse,
  Aggregation,
  AggregationValue,
  JobEntity,
} from '@/types/job.types';
import type { CandidateDetails } from '@/services/api/authApi';
import type { LoginReturnInput } from '@/services/auth/postLoginRedirect';
import { ROUTES } from '@/app/router/routes';
import { redirectToSignIn, SIGN_IN_TO_SAVE_JOB } from '../utils/signInRequired';

// Toast texts — Flutter ErrorConstants (Toast translates them)
const SAVE_JOB_SUCCESS = 'Saved the job successfully';
const SAVE_JOB_FAILED = 'Failed to save the job!';
const UNSAVE_JOB_SUCCESS = 'Unsaved the job successfully';
const UNSAVE_JOB_FAILED = 'Failed to unsave the job!';
const NO_CONTENT =
  'No data is extracted from your resume. Please upload new resume or enter skills above to search for jobs.';
const FILE_EXTRACTION_FAILED = 'Some error occurred while extracting the resume';

/** Flutter JobListViewBloc analytics location */
const JOB_LIST_VIEW = 'Job List View';

/**
 * Default-sort input check (Flutter _SetPredefinedFilters): a category search, or one with
 * skills / roles, sorts by Relevance; anything else by Posting Date.
 */
function sortsByRelevance(input: SearchInput): boolean {
  return input.categoryDetails != null || hasSearchTerms(input);
}

/**
 * Flutter _SetPredefinedFilters: the search input's filters (when present) become the
 * selection, then the default sort replaces any sort in it.
 */
function predefinedFilters(input: SearchInput): SelectedFilters {
  const filters: SelectedFilters = {};
  for (const [key, values] of Object.entries(input.filters ?? {})) {
    if (key === 'sortBy' || key === 'selectedSortField' || key === 'selectedSortOrder') continue;
    if (values.length) filters[key] = values;
  }
  return { ...filters, ...defaultSortFilters(sortsByRelevance(input)) };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/** Flutter JobListViewState.maxLimit */
const PAGE_SIZE = 6;
/** Flutter scroll listener: load the next page within 850px of the end */
const LOAD_MORE_DELTA = 850;

export type JobsListToast = { message: string; type: 'success' | 'error' | 'info' };

export interface JobsListState {
  searchInput: SearchInput;
  entities: JobEntity[];
  /** Aggregations with the staged selection checked */
  aggregations: Aggregation[];
  /** Filter dropdowns — only the tenant's supported filters (Flutter FilterContainer) */
  filterAggregations: Aggregation[];
  sortAggregation: Aggregation | undefined;
  totalJobsCount: number;
  hasMore: boolean;
  isCardView: boolean;
  isLoading: boolean;
  isLoadingJobList: boolean;
  isLoadingMore: boolean;
  selectedFilters: SelectedFilters;
  candidateDetails: CandidateDetails | null;
  toast: JobsListToast | null;
  categoryDetails: Record<string, string> | null;
  /** Upload Resume: data-privacy consent dialog */
  showDataPrivacy: boolean;
  /** Upload Resume: analyzer dialog while the resume is extracted */
  isAnalyzing: boolean;
}

export interface JobsListActions {
  onClickGo: (input: SearchInput) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
  onFilterSelection: (listOfFilters: AggregationValue[]) => void;
  /** Sort chosen from the sort menu — applied immediately */
  onSelectSort: (value: AggregationValue) => void;
  onClearFilter: (key: string) => void;
  onToggleCardView: () => void;
  onSaveJob: (entity: JobEntity, index: number) => void;
  onUnsaveJob: (entity: JobEntity, index: number) => void;
  /** Upload Resume button — `openFilePicker` opens the page's file input */
  onClickUploadResume: (openFilePicker: () => void) => void;
  onDataPrivacyAgree: (openFilePicker: () => void) => void;
  onDataPrivacyCancel: () => void;
  onResumeSelected: (file: File) => void;
  clearToast: () => void;
}

/**
 * Jobs list logic — mirrors Flutter JobListViewBloc. Every search goes through
 * _OnApplyFilters (applyFilters below): it logs `search` / "apply filter" and loads the first
 * page and the aggregations.
 */
export function useJobsList(): { state: JobsListState; actions: JobsListActions } {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const preferences = useTenantStore((s) => s.preferences);
  const currentLanguage = useLocalizationStore((s) => s.currentLanguage);

  // Route state: Home search (SearchInput) or the return from login (ProfileInput)
  const routeState = location.state as {
    searchInput?: SearchInput;
    profileInput?: LoginReturnInput;
  } | null;
  const saveJobInProgress = !!routeState?.profileInput?.saveJobInProgress;

  // ── Core state ──
  const [searchInput, setSearchInput] = useState<SearchInput>(
    routeState?.searchInput ?? { isHotJob: false, switchIndex: 0 },
  );
  const [entities, setEntities] = useState<JobEntity[]>([]);
  const [rawAggregations, setRawAggregations] = useState<Aggregation[]>([]);
  const [totalJobsCount, setTotalJobsCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isCardView, setIsCardView] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingJobList, setIsLoadingJobList] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({});
  const [candidateDetails, setCandidateDetails] = useState<CandidateDetails | null>(null);
  const [toast, setToast] = useState<JobsListToast | null>(null);
  const [showDataPrivacy, setShowDataPrivacy] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const isInitialized = useRef(false);
  const isFetching = useRef(false);
  /** Ignores responses from a search that a newer one replaced */
  const searchSeq = useRef(0);
  /** The latest page request; only it may clear the loading flags */
  const latestRequest = useRef(0);
  const candidateRef = useRef<CandidateDetails | null>(null);
  const saveAfterLoginDone = useRef(false);

  // ── Fetch jobs ──
  const fetchJobs = useCallback(
    async (input: SearchInput, page: number, append = false) => {
      const seq = append ? searchSeq.current : ++searchSeq.current;
      const requestId = ++latestRequest.current;
      if (page === 1) setIsLoadingJobList(true);
      else setIsLoadingMore(true);

      try {
        const res: SearchResponse = await searchJobs(input, page, PAGE_SIZE);
        if (seq !== searchSeq.current) return;
        const total = res.total ?? 0;
        const pageEntities = res.entities ?? [];
        setTotalJobsCount(total);
        setHasMore(total > page * PAGE_SIZE);
        // Flutter _GetSavedAppliedJobs after every page: the cached status first, then the network
        const cached = readAppliedSavedCache();
        setEntities((prev) => withAppliedSaved(append ? appendNewJobs(prev, pageEntities) : pageEntities, cached));
        setIsLoadingJobList(false);
        setIsLoadingMore(false);
        // The page is in: the next one may load while the applied/saved statuses are fetched.
        // (Left set until `finally`, the scroll check that runs for the new cards was ignored and
        // a list already scrolled to its end stopped short.)
        isFetching.current = false;

        if (auth.currentUser) {
          try {
            const appliedSaved = await getAppliedSavedJobs();
            if (seq !== searchSeq.current) return;
            setEntities((prev) => withAppliedSaved(prev, appliedSaved));
          } catch {
            // Non-critical
          }
        }
      } catch {
        if (seq !== searchSeq.current) return;
        if (!append) setEntities([]);
        setHasMore(false);
      } finally {
        // A later page may already be loading (isFetching is cleared once a page is in)
        if (seq === searchSeq.current && requestId === latestRequest.current) {
          setIsLoadingJobList(false);
          setIsLoadingMore(false);
          setIsLoading(false);
          isFetching.current = false;
        }
      }
    },
    [],
  );

  // ── Fetch aggregations (Flutter _GetSearchResultAggregation → _SetFilterBackup) ──
  const fetchAggregations = useCallback(async (input: SearchInput, filters: SelectedFilters) => {
    const seq = searchSeq.current;
    try {
      const res = await getSearchAggregations(input);
      if (seq !== searchSeq.current) return;
      setRawAggregations(sortSelectedOnTop(res.aggregations ?? [], filters));
    } catch {
      // Non-critical
    }
  }, []);

  /** Flutter _OnApplyFilters: search with `filters` as the selection. */
  const applyFilters = useCallback(
    (input: SearchInput, filters: SelectedFilters) => {
      void analyticsService.logJobSearch({ searchTerm: 'apply filter', filters });
      const next: SearchInput = { ...input, filters };
      setSelectedFilters(filters);
      setSearchInput(next);
      setCurrentPage(1);
      setHasMore(true);
      fetchJobs(next, 1);
      fetchAggregations(next, filters);
    },
    [fetchJobs, fetchAggregations],
  );

  const refreshSavedJobsCount = useCallback(() => {
    // Updates the header badge (Flutter AppStateNotifier.changeJobCount)
    getSavedJobsCount().catch(() => {});
  }, []);

  // ── Complete save-after-login — Flutter JobListViewBloc._OnClickSaveJobAfterLogin ──
  const completeSaveAfterLogin = useCallback(
    async (details: CandidateDetails) => {
      // Nothing pending (e.g. a refresh: unlike Flutter's route arguments, history state
      // survives it) — nothing to report
      const saveJobJson = localStorage.getItem('saveJobDetails');
      if (!saveJobJson || !auth.currentUser) return;
      // One attempt per login, so a failed save doesn't replay on a later visit
      localStorage.removeItem('saveJobDetails');

      try {
        const decodedMap = JSON.parse(saveJobJson) as Record<string, unknown>;
        if (!decodedMap.requisitionId) throw new Error('No pending save');
        const candidate = details as Record<string, unknown>;
        await saveJob({
          candidateId: candidate.id,
          requisitionId: decodedMap.requisitionId,
          candidateDisplayId: candidate.displayId,
          requisitionDisplayId: decodedMap.requisitionDisplayId,
          primaryEmailId: details.primaryEmailId,
          exprncFromMnthInYears: decodedMap.exprncFromMnthInYears,
          exprncToMnthInYears: decodedMap.exprncToMnthInYears,
        });

        setEntities((prev) =>
          prev.map((e) =>
            e.displayId === decodedMap.requisitionDisplayId ? { ...e, isSavedJob: true } : e,
          ),
        );
        setToast({ message: SAVE_JOB_SUCCESS, type: 'success' });
        refreshSavedJobsCount();
      } catch {
        setToast({ message: SAVE_JOB_FAILED, type: 'error' });
      }
    },
    [refreshSavedJobsCount],
  );

  // ── Init — Flutter _Init → _SetPredefinedFilters ──
  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    const initialInput: SearchInput = routeState?.searchInput ?? { isHotJob: false, switchIndex: 0 };
    applyFilters(initialInput, predefinedFilters(initialInput));
  }, [applyFilters, routeState]);

  // ── Signed-in user: candidate details, then the pending save or the saved-jobs count ──
  useEffect(() => {
    if (!user) {
      candidateRef.current = null;
      setCandidateDetails(null);
      return;
    }
    if (!saveJobInProgress) refreshSavedJobsCount();
    getUserDetails()
      .then((d) => {
        candidateRef.current = d;
        setCandidateDetails(d);
        // Flutter _GetUserDetails → _OnClickSaveJobAfterLogin
        if (saveJobInProgress && !saveAfterLoginDone.current) {
          saveAfterLoginDone.current = true;
          void completeSaveAfterLogin(d);
        }
      })
      .catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Language change — Flutter languageStream → _SetPredefinedFilters ──
  const languageRef = useRef(currentLanguage);
  useEffect(() => {
    if (languageRef.current === currentLanguage) return;
    languageRef.current = currentLanguage;
    applyFilters(searchInput, predefinedFilters(searchInput));
  }, [currentLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Infinite scroll — Flutter scrollController listener → _OnLoadMore ──
  const loadMore = useCallback(() => {
    if (!hasMore || isFetching.current || isLoadingMore || isLoadingJobList) return;
    isFetching.current = true;
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchJobs(searchInput, nextPage, true);
  }, [hasMore, isLoadingMore, isLoadingJobList, currentPage, searchInput, fetchJobs]);

  useEffect(() => {
    const check = () => {
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
      const clientHeight = document.documentElement.clientHeight || window.innerHeight;
      if (scrollHeight - scrollTop - clientHeight <= LOAD_MORE_DELTA) loadMore();
    };
    window.addEventListener('scroll', check);
    // A page that is already within reach of its end (no scrolling possible) keeps loading too
    if (entities.length > 0) check();
    return () => window.removeEventListener('scroll', check);
  }, [loadMore, entities.length]);

  // ── Actions ──

  // Flutter _OnClickGo: the search bar's new input (no category, hot-job flag or filters)
  // then _SetPredefinedFilters. The search bar's input carries only the default sort, which
  // replaces the selection — so a new search starts from the default sort with no filters.
  const onClickGo = useCallback(
    (input: SearchInput) => {
      void analyticsService.logJobSearch({ searchTerm: 'job search', filters: searchInput.filters });
      setIsLoading(true);
      const newInput: SearchInput = { ...input, filters: undefined, categoryDetails: undefined };
      applyFilters(newInput, predefinedFilters(newInput));
    },
    [searchInput.filters, applyFilters],
  );

  const onApplyFilters = useCallback(() => {
    applyFilters(searchInput, selectedFilters);
  }, [searchInput, selectedFilters, applyFilters]);

  // Flutter _ClearFilters: drops filters, category and the hot-job flag
  const onClearFilters = useCallback(() => {
    setIsLoading(true);
    const cleared: SearchInput = {
      ...searchInput,
      filters: undefined,
      categoryDetails: undefined,
      isHotJob: undefined,
    };
    void analyticsService.logEvent('home_page', 'clear_filter');
    applyFilters(cleared, predefinedFilters(cleared));
  }, [searchInput, applyFilters]);

  // Flutter _OnFilterSelection — staged until Apply
  const onFilterSelection = useCallback((listOfFilters: AggregationValue[]) => {
    setSelectedFilters((prev) => applyFilterSelection(prev, listOfFilters));
  }, []);

  // Flutter CustomJobListSortByFilterWidget: onClickOption then onApplyFilters
  const onSelectSort = useCallback(
    (value: AggregationValue) => {
      applyFilters(searchInput, applyFilterSelection(selectedFilters, [value]));
    },
    [searchInput, selectedFilters, applyFilters],
  );

  // Flutter _OnClearFilter — removes one category; the search reruns on Apply
  const onClearFilter = useCallback((key: string) => {
    setSelectedFilters((prev) => {
      if (!(key in prev)) return prev;
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  }, []);

  const onToggleCardView = useCallback(() => {
    setIsCardView((prev) => !prev);
  }, []);

  const setSaveLoading = useCallback((index: number, isLoadingForSaveJobButton: boolean) => {
    setEntities((prev) => {
      if (!prev[index]) return prev;
      const copy = [...prev];
      copy[index] = { ...copy[index], isLoadingForSaveJobButton };
      return copy;
    });
  }, []);

  // Flutter _OnClickSaveJob
  const onSaveJob = useCallback(
    async (entity: JobEntity, index: number) => {
      if (!auth.currentUser) {
        // Anonymous save — remember the job and log in first
        localStorage.setItem(
          'saveJobDetails',
          JSON.stringify({
            requisitionId: entity.id,
            requisitionDisplayId: entity.displayId,
            exprncFromMnthInYears: entity.requiredExperienceInMonths?.from,
            exprncToMnthInYears: entity.requiredExperienceInMonths?.to,
          }),
        );
        const profileInput: LoginReturnInput = { saveJobInProgress: true, previousPage: ROUTES.JOBS };
        redirectToSignIn(navigate, SIGN_IN_TO_SAVE_JOB, profileInput);
        return;
      }

      setSaveLoading(index, true);
      try {
        const details = candidateRef.current ?? (await getUserDetails());
        candidateRef.current = details;
        const candidate = details as Record<string, unknown>;
        await saveJob({
          candidateId: candidate.id,
          requisitionId: entity.id,
          candidateDisplayId: candidate.displayId,
          requisitionDisplayId: entity.displayId,
          primaryEmailId: details.primaryEmailId,
          exprncFromMnthInYears: entity.requiredExperienceInMonths?.from,
          exprncToMnthInYears: entity.requiredExperienceInMonths?.to,
        });

        setEntities((prev) => {
          if (!prev[index]) return prev;
          const copy = [...prev];
          copy[index] = { ...copy[index], isSavedJob: true, isLoadingForSaveJobButton: false };
          return copy;
        });
        void analyticsService.logJobSave({
          location: JOB_LIST_VIEW,
          jobId: entity.id ?? '',
          jobTitle: entity.jobTitle ?? '',
        });
        setToast({ message: SAVE_JOB_SUCCESS, type: 'success' });
      } catch {
        setSaveLoading(index, false);
        setToast({ message: SAVE_JOB_FAILED, type: 'error' });
      }
      refreshSavedJobsCount();
    },
    [navigate, setSaveLoading, refreshSavedJobsCount],
  );

  // Flutter _OnClickUnsaveJob — logs job_unsave whether or not it worked
  const onUnsaveJob = useCallback(
    async (entity: JobEntity, index: number) => {
      setSaveLoading(index, true);
      try {
        if (!(await unsaveJob(entity.displayId ?? ''))) throw new Error('Unsave failed');
        setEntities((prev) => {
          if (!prev[index]) return prev;
          const copy = [...prev];
          copy[index] = { ...copy[index], isSavedJob: false, isLoadingForSaveJobButton: false };
          return copy;
        });
        setToast({ message: UNSAVE_JOB_SUCCESS, type: 'success' });
      } catch {
        setSaveLoading(index, false);
        setToast({ message: UNSAVE_JOB_FAILED, type: 'error' });
      }
      void analyticsService.logJobUnsave({
        location: JOB_LIST_VIEW,
        jobId: entity.id ?? '',
        jobTitle: entity.jobTitle ?? '',
      });
      refreshSavedJobsCount();
    },
    [setSaveLoading, refreshSavedJobsCount],
  );

  // ── Upload Resume — Flutter JobListView._buildUploadResumeButtonWidget ──
  // Agreed before → click_on_upload_resume + file picker; otherwise the consent dialog first
  // (Flutter logs nothing on that path).
  const onClickUploadResume = useCallback((openFilePicker: () => void) => {
    if (isAgreedWithDataPolicy()) {
      void analyticsService.logEvent('career_page', 'click_on_upload_resume');
      openFilePicker();
      return;
    }
    setShowDataPrivacy(true);
  }, []);

  const onDataPrivacyAgree = useCallback((openFilePicker: () => void) => {
    setShowDataPrivacy(false);
    setAgreedWithDataPolicy();
    openFilePicker();
  }, []);

  const onDataPrivacyCancel = useCallback(() => setShowDataPrivacy(false), []);

  // Flutter _SelectFilesFromSystem → _ExtractResume → /search_by_resume
  const onResumeSelected = useCallback(
    async (file: File) => {
      const fileError = validateResumeFile(file);
      if (fileError) {
        setToast({ message: fileError, type: 'error' });
        return;
      }

      setIsAnalyzing(true);
      try {
        const result = await extractResumeSkills(file);
        if (!result || typeof result !== 'object') {
          setToast({ message: NO_CONTENT, type: 'error' });
          return;
        }
        const extractData = { ...(result as Record<string, unknown>), fileName: file.name };
        sessionStorage.setItem(EXTRACTED_RESUME_KEY, JSON.stringify(extractData));
        void analyticsService.logEvent('job_list_view', 'extract_resume');
        void analyticsService.logScreenView({ screenName: ROUTES.EXTRACTED_RESUME });
        navigate(ROUTES.EXTRACTED_RESUME);
      } catch {
        setToast({ message: FILE_EXTRACTION_FAILED, type: 'error' });
      } finally {
        setIsAnalyzing(false);
      }
    },
    [navigate],
  );

  const clearToast = useCallback(() => setToast(null), []);

  // ── Derived ──
  const aggregations = useMemo(
    () => withCheckedState(rawAggregations, selectedFilters),
    [rawAggregations, selectedFilters],
  );
  const filterAggregations = useMemo(() => {
    const supported = getSupportedFilterKeys(preferences as Record<string, unknown> | null);
    return aggregations.filter((a) => supported.includes(a.name ?? ''));
  }, [aggregations, preferences]);
  const sortAggregation = aggregations.find((a) => a.name === 'sortBy');

  return {
    state: {
      searchInput,
      entities,
      aggregations,
      filterAggregations,
      sortAggregation,
      totalJobsCount,
      hasMore,
      isCardView,
      isLoading,
      isLoadingJobList,
      isLoadingMore,
      selectedFilters,
      candidateDetails,
      toast,
      categoryDetails: searchInput.categoryDetails ?? null,
      showDataPrivacy,
      isAnalyzing,
    },
    actions: {
      onClickGo,
      onApplyFilters,
      onClearFilters,
      onFilterSelection,
      onSelectSort,
      onClearFilter,
      onToggleCardView,
      onSaveJob,
      onUnsaveJob,
      onClickUploadResume,
      onDataPrivacyAgree,
      onDataPrivacyCancel,
      onResumeSelected,
      clearToast,
    },
  };
}
